import { MedfarDictionariesValidationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/MedfarDictionariesValidationRule";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { DisplacedDirectoryFormRow } from "./DisplacedDirectoryFormRow";
import { RemoveContainer } from "./RemoveContainer";

import commonStyles from "../FaxDirectoryForm.module.scss";

interface IDirectoryContactNumberProps {
    id: string;

    // Contact number
    number: string;
    onNumberChange: (number: string) => void;

    // Country field
    selectedCountryId: string;
    countryOptions: KeyValuePair<string, string>[];
    onSelectCountryId: (countryId: string) => void;

    // Extension
    extension?: string;
    onExtensionChange?: (extension: string) => void;

    // Description field
    description?: string;
    onDescriptionChange?: (description: string) => void;

    isValid: boolean;
    isFax: boolean;
    isDuplicateInDirectory?: boolean;
    isModifiedCountryId?: boolean;
    validationRule: MedfarDictionariesValidationRule | null;
    onRemove?: () => void;
    onCheckForDuplicates?: () => void;
    isDuplicateInForm?: boolean;

    addRightSpacing?: boolean;
}

export function DirectoryContactNumber(props: IDirectoryContactNumberProps): JSX.Element {
    const { getText } = useTranslation();
    const isDuplicate = props.isDuplicateInDirectory || props.isDuplicateInForm;
    const numberLabel = props.isFax ? "FaxNumberLabel" : "ContactDirectory_PhoneNumberLabel";

    return (
        <>
            {props.validationRule ? (
                <RemoveContainer onRemove={props.onRemove} className="margin-md-bottom" key={props.id}>
                    <div>
                        <DisplacedDirectoryFormRow
                            labelKey="CountryLabel"
                            className={props.addRightSpacing ? commonStyles.removeableRightSpacing : ""}
                        >
                            <SelectKVPControl
                                Value={props.selectedCountryId}
                                AvailableValues={props.countryOptions}
                                IncludeEmptyChoice={false}
                                Callback={countryId => props.onSelectCountryId(countryId)}
                            />
                        </DisplacedDirectoryFormRow>
                        <div className="flex-display flex-col-gap-md flex-align-items-center">
                            <DisplacedDirectoryFormRow
                                labelKey={numberLabel}
                                className={isDuplicate ? commonStyles.noBottomPadding : ""}
                            >
                                <div style={{ width: "200px" }}>
                                    <TextboxMaskedControl
                                        isValid={props.isValid}
                                        id={`number-other-establishment-${props.id}`}
                                        allowEmpty={false}
                                        value={props.number}
                                        onChangeFunction={number => props.onNumberChange(number)}
                                        mask={props.validationRule.EditMaskPattern}
                                        onBlurFunction={() => props.onCheckForDuplicates?.()}
                                    />
                                </div>
                            </DisplacedDirectoryFormRow>
                            <div style={{ width: "150px" }}>
                                {props.onExtensionChange && (
                                    <DisplacedDirectoryFormRow
                                        labelKey="ExtLabel"
                                        className={isDuplicate ? commonStyles.noBottomPadding : ""}
                                    >
                                        <input
                                            className={`form-control input ${isDuplicate ? "invalid" : ""}`}
                                            onChange={e => props.onExtensionChange(e.target.value.replace(/[^0-9]+/, ""))}
                                            maxLength={10}
                                            value={props.extension}
                                        />
                                    </DisplacedDirectoryFormRow>
                                )}
                            </div>
                        </div>
                        {isDuplicate && (
                            <div className="red-text margin-sm-bottom invalidDuplicateNumber">
                                {getText(props.isDuplicateInDirectory ? "FieldCannotBeReusedInTwoEntriesLabel" : "NumberCannotBeReusedInCurrentEntryLabel", { field: getText("NumberLabel") })}
                            </div>
                        )}
                        {props.onDescriptionChange && (
                            <DisplacedDirectoryFormRow
                                labelKey="DescriptionLabel"
                                className={props.addRightSpacing ? commonStyles.removeableRightSpacing : ""}
                            >
                                <TextboxControl
                                    value={props.description}
                                    onChange={description => props.onDescriptionChange(description)}
                                    maxLength={250}
                                />
                            </DisplacedDirectoryFormRow>
                        )}
                    </div>
                </RemoveContainer>
            )
                : null
            }
        </>
    );
}
